import {
  FileImageIcon,
  FloppyDiskIcon,
  TrashIcon,
  UploadSimpleIcon,
} from "@phosphor-icons/react";
import {
  type ChangeEvent,
  type DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { ApiError, apiRequest } from "../api/client";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { LoadingState } from "../components/LoadingState";
import { PageHeader } from "../components/PageHeader";
import type { MediaAsset, MediaUploadBatch } from "../types";

export function MediaPage() {
  const [assets, setAssets] = useState<MediaAsset[] | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadingCount, setUploadingCount] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const filteredAssets = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!assets) return null;
    if (!normalized) return assets;
    return assets.filter((asset) =>
      [
        asset.original_name,
        asset.alt_text,
        asset.mime_type,
        asset.used_in.map((usage) => usage.label).join(" "),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalized),
    );
  }, [assets, query]);
  const load = useCallback(() => {
    apiRequest<MediaAsset[]>("/api/v1/admin/media")
      .then(setAssets)
      .catch(() => setError("Не удалось загрузить медиатеку"));
  }, []);
  useEffect(load, [load]);

  const uploadFiles = useCallback(async (files: File[]) => {
    if (!files.length) return;
    if (files.length > 30) {
      setError("За раз можно загрузить до 30 файлов.");
      return;
    }
    setUploadingCount(files.length);
    setError("");
    setSuccess("");
    const body = new FormData();
    files.forEach((file) => body.append("files", file));
    body.set("alt_text", "");
    try {
      const result = await apiRequest<MediaUploadBatch>(
        "/api/v1/admin/media/upload-batch",
        { method: "POST", body },
      );
      if (result.assets.length) {
        setAssets((current) => [...result.assets, ...(current ?? [])]);
        setSuccess(
          `${result.assets.length} ${pluralFiles(result.assets.length)} сконвертированы в WebP. Исходники не сохранены.`,
        );
      }
      if (result.failures.length) {
        setError(
          result.failures
            .map((failure) => `${failure.filename}: ${failure.detail}`)
            .join(" · "),
        );
      }
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось загрузить файл",
      );
    } finally {
      setUploadingCount(null);
    }
  }, []);

  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    await uploadFiles(files);
  }

  function handleDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    if (uploadingCount) return;
    void uploadFiles(Array.from(event.dataTransfer.files));
  }

  async function updateAlt(asset: MediaAsset, altText: string) {
    setError("");
    setSuccess("");
    try {
      const updated = await apiRequest<MediaAsset>(
        `/api/v1/admin/media/${asset.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({ alt_text: altText }),
        },
      );
      setAssets((current) =>
        (current ?? []).map((item) =>
          item.id === updated.id ? updated : item,
        ),
      );
      setSuccess("Alt-текст сохранён");
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось сохранить alt",
      );
    }
  }

  async function deleteAsset(asset: MediaAsset) {
    if (!asset.can_delete) {
      setError(
        "Файл используется в CMS-контенте — сначала замените его в секциях.",
      );
      return;
    }
    const confirmed = window.confirm(`Удалить ${asset.original_name}?`);
    if (!confirmed) return;
    setError("");
    setSuccess("");
    try {
      await apiRequest(`/api/v1/admin/media/${asset.id}`, { method: "DELETE" });
      setAssets((current) =>
        (current ?? []).filter((item) => item.id !== asset.id),
      );
      setSuccess("Файл удалён из медиатеки и storage");
    } catch (reason) {
      setError(
        reason instanceof ApiError ? reason.message : "Не удалось удалить файл",
      );
    }
  }

  return (
    <div className="page">
      <PageHeader
        actions={
          <label className="button button--primary">
            <UploadSimpleIcon size={17} />{" "}
            {uploadingCount
              ? `Обрабатываем ${uploadingCount}…`
              : "Загрузить файлы"}
            <input
              accept="image/avif,image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
              disabled={Boolean(uploadingCount)}
              hidden
              multiple
              onChange={(event) => void upload(event)}
              type="file"
            />
          </label>
        }
        description="Загрузите PNG, JPEG, WebP или видео-источник — система сохранит только WebP-изображение/постер. Исходники не хранятся."
        eyebrow="ASSETS"
        title="Медиатека"
      />
      {error && <div className="notice notice--error">{error}</div>}
      {success && <div className="notice notice--success">{success}</div>}
      <label
        className={`media-dropzone${uploadingCount ? "is-uploading" : ""}`}
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleDrop}
      >
        <FileImageIcon size={23} />
        <span>
          <strong>
            {uploadingCount
              ? `Конвертируем ${uploadingCount} файлов в WebP…`
              : "Перетащите файлы сюда или выберите с компьютера"}
          </strong>
          <small>
            До 30 файлов за раз, до 15 MB каждый · общий объём до 256 MB · AVIF,
            JPEG, PNG, WebP, MP4, MOV, WebM
          </small>
        </span>
        <em>WebP only</em>
        <input
          accept="image/avif,image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
          disabled={Boolean(uploadingCount)}
          multiple
          onChange={(event) => void upload(event)}
          type="file"
        />
      </label>
      {assets ? (
        <div className="table-toolbar media-toolbar">
          <label>
            <span>Поиск</span>
            <input
              onChange={(event) => setQuery(event.target.value)}
              placeholder="имя, alt, где используется"
              value={query}
            />
          </label>
          <strong>{filteredAssets?.length ?? 0} файлов</strong>
          <span className="webp-label">Storage: WebP only</span>
        </div>
      ) : null}
      {!assets ? (
        <LoadingState />
      ) : assets.length === 0 ? (
        <EmptyState
          description="Файлы будут доступны здесь для выбора в компонентах страниц."
          title="Медиатека пуста"
        />
      ) : (
        <section className="media-grid" aria-label="Загруженные изображения">
          {filteredAssets?.map((asset) => (
            <article className="media-card" key={asset.id}>
              <img alt={asset.alt_text || ""} src={asset.url} />
              <div className="media-card__body">
                <header>
                  <strong>{asset.original_name}</strong>
                  <span>
                    {asset.width} × {asset.height} · WebP ·{" "}
                    {(asset.byte_size / 1024).toFixed(0)} KB
                  </span>
                </header>
                <AltEditor asset={asset} onSave={updateAlt} />
                <div className="media-card__usage">
                  <span>Используется</span>
                  {asset.used_in.length ? (
                    <ul>
                      {asset.used_in.slice(0, 4).map((usage, index) => (
                        <li key={`${usage.label}-${index}`}>
                          {usage.locale
                            ? `${usage.locale.toUpperCase()} · `
                            : ""}
                          {usage.label}
                        </li>
                      ))}
                      {asset.used_in.length > 4 ? (
                        <li>+ ещё {asset.used_in.length - 4}</li>
                      ) : null}
                    </ul>
                  ) : (
                    <p>Нигде не используется</p>
                  )}
                </div>
                <Button
                  disabled={!asset.can_delete}
                  onClick={() => void deleteAsset(asset)}
                  tone={asset.can_delete ? "danger" : "quiet"}
                >
                  <TrashIcon size={15} />
                  {asset.can_delete ? "Удалить" : "Используется"}
                </Button>
              </div>
            </article>
          ))}
          {filteredAssets?.length === 0 ? (
            <div className="inline-empty">По фильтрам ничего не найдено.</div>
          ) : null}
        </section>
      )}
    </div>
  );
}

function pluralFiles(count: number) {
  const remainder = count % 10;
  const hundreds = count % 100;
  if (remainder === 1 && hundreds !== 11) return "файл";
  if (remainder >= 2 && remainder <= 4 && (hundreds < 12 || hundreds > 14))
    return "файла";
  return "файлов";
}

function AltEditor({
  asset,
  onSave,
}: {
  asset: MediaAsset;
  onSave: (asset: MediaAsset, altText: string) => Promise<void>;
}) {
  const [value, setValue] = useState(asset.alt_text);
  useEffect(() => setValue(asset.alt_text), [asset.alt_text]);
  const dirty = value.trim() !== asset.alt_text;
  return (
    <label className="media-alt">
      <span>Alt</span>
      <div>
        <input
          onChange={(event) => setValue(event.target.value)}
          placeholder="Краткое описание изображения"
          value={value}
        />
        <button
          disabled={!dirty}
          onClick={() => void onSave(asset, value)}
          title="Сохранить alt"
          type="button"
        >
          <FloppyDiskIcon size={15} />
        </button>
      </div>
    </label>
  );
}
